import { useCallback, useEffect, useState } from 'react';
import type { FormEvent, ReactNode } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { api } from '../api/endpoints';
import type { Batch, CustomerCard, Pool, RefundMode, SessionHistory } from '../api/types';
import { useAuth } from '../auth/AuthContext';
import { useDataVersion } from '../hooks/useDataVersion';
import {
  formatDate,
  formatDateTime,
  formatBalance,
  formatMinutes,
  formatMoney,
  formatTime,
  creditLabel,
  owedAfterCredit,
  POOL_LABEL,
  POOL_LABEL_INLINE,
  STATION_TYPE_LABEL,
  totalPlayedSeconds,
} from '../lib/format';
import { SellRechargeDialog } from '../components/SellRechargeDialog';
import { CustomerTabSection } from '../components/CustomerTab';
import { PurchaseHistorySection } from '../components/PurchaseHistory';
import { SeatCustomerDialog } from '../components/SeatCustomerDialog';
import { useToast } from '../components/Toast';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorNote,
  Input,
  Modal,
  MONEY_MASK,
  RevealButton,
  Spinner,
} from '../components/ui';

function EditCustomerDialog({
  customer,
  onClose,
  onSaved,
}: {
  customer: CustomerCard;
  onClose: () => void;
  onSaved: () => void;
}) {
  const toast = useToast();
  const [name, setName] = useState(customer.name);
  const [phone, setPhone] = useState(customer.phone);
  const [notes, setNotes] = useState(customer.notes ?? '');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.customers.update(customer.customer_id, {
        name: name.trim(),
        phone: phone.trim(),
        notes: notes.trim(),
      });
      toast.success('Customer updated.');
      onSaved();
      onClose();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const toggleActive = async () => {
    setBusy(true);
    try {
      await api.customers.update(customer.customer_id, {
        is_active: !customer.is_active,
      });
      toast.success(
        customer.is_active ? 'Customer deactivated.' : 'Customer reactivated.',
      );
      onSaved();
      onClose();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Edit customer" onClose={onClose}>
      <form onSubmit={submit} className="space-y-4">
        {error && <ErrorNote>{error}</ErrorNote>}
        <Input
          label="Name"
          value={name}
          onChange={(event) => setName(event.target.value)}
          required
        />
        <Input
          label="Phone"
          value={phone}
          onChange={(event) => setPhone(event.target.value)}
          inputMode="numeric"
          required
        />
        <Input
          label="Notes"
          value={notes}
          onChange={(event) => setNotes(event.target.value)}
        />
        <div className="flex flex-col gap-2 sm:flex-row">
          <Button type="submit" className="flex-1" disabled={busy}>
            Save
          </Button>
          <Button
            type="button"
            variant={customer.is_active ? 'danger' : 'secondary'}
            className="flex-1"
            disabled={busy}
            onClick={toggleActive}
          >
            {customer.is_active ? 'Deactivate account' : 'Reactivate account'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

// Must match DEFAULT_CUSTOMER_PASSWORD in the backend's LoginHelpers.ts.
const DEFAULT_PASSWORD = '1234';

// A suggested username from the customer's name: lower-case, no spaces, only
// the characters the backend accepts. Staff can change it before saving.
function suggestUsername(name: string): string {
  return name
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '.')
    .replace(/[^a-z0-9._-]/g, '')
    .slice(0, 32);
}

// Station sign-in: the username and password a customer types on a free PC's
// lock screen to start their own session from the time already on their
// account. Also where a forgotten password is reset — the customer asks at
// the counter, staff type a new one here. The current password is never
// shown; there is nothing to show, only a hash on the server.
function StationLoginDialog({
  customer,
  onClose,
  onSaved,
}: {
  customer: CustomerCard;
  onClose: () => void;
  onSaved: () => void;
}) {
  const toast = useToast();
  const hasLogin = customer.station_login;
  const [username, setUsername] = useState(
    customer.username ?? suggestUsername(customer.name),
  );
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    if (password !== confirm) {
      setError('The two passwords do not match.');
      return;
    }
    setBusy(true);
    try {
      await api.customers.setLogin(customer.customer_id, {
        username: username.trim(),
        ...(password ? { password } : {}),
      });
      toast.success(
        password
          ? `${customer.name} can sign in at a station as "${username.trim()}".`
          : 'Username updated.',
      );
      onSaved();
      onClose();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  // The counter's answer to "I forgot my password": back to the default,
  // which the customer then changes on the lock screen.
  const resetToDefault = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.customers.setLogin(customer.customer_id, {
        username: username.trim(),
        password: DEFAULT_PASSWORD,
      });
      toast.success(`${customer.name}'s password is now ${DEFAULT_PASSWORD}.`);
      onSaved();
      onClose();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Station sign-in" onClose={onClose}>
      <form onSubmit={submit} className="space-y-4">
        <p className="text-sm text-slate-400">
          With a username and password, {customer.name} can start their own
          session from any free PC's lock screen, using time already on their
          account. They can change the password there too. Forgotten
          passwords are reset here.
        </p>
        {error && <ErrorNote>{error}</ErrorNote>}
        <Input
          label="Username"
          value={username}
          onChange={(event) => setUsername(event.target.value)}
          autoComplete="off"
          required
        />
        <Input
          label={
            hasLogin
              ? 'New password (leave blank to keep the current one)'
              : `Password (leave blank for ${DEFAULT_PASSWORD})`
          }
          type="password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          autoComplete="new-password"
          minLength={4}
          maxLength={72}
        />
        <Input
          label="Confirm password"
          type="password"
          value={confirm}
          onChange={(event) => setConfirm(event.target.value)}
          autoComplete="new-password"
          required={password.length > 0}
        />
        <div className="flex flex-col gap-2 sm:flex-row">
          <Button type="submit" className="flex-1" disabled={busy}>
            Save
          </Button>
          <Button
            type="button"
            variant="secondary"
            className="flex-1"
            disabled={busy}
            onClick={resetToDefault}
          >
            Reset password to {DEFAULT_PASSWORD}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

// Corrections to prepaid time, as a signed delta rather than a new total.
//
// Staff arrive at this dialog thinking "give them back the twenty minutes the
// crash ate", not "set this batch to 47". The delta is also what makes the
// change safe against the billing engine: if a minute is spent between opening
// this and saving, +20 is still +20, where a total would quietly refund it.
function AdjustBatchDialog({
  batch,
  customerName,
  onClose,
  onSaved,
}: {
  batch: Batch;
  customerName: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const toast = useToast();
  const [amount, setAmount] = useState('');
  const [direction, setDirection] = useState<'add' | 'subtract'>('add');
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const magnitude = Number(amount);
  const delta = direction === 'add' ? magnitude : -magnitude;
  const valid =
    Number.isInteger(magnitude) && magnitude > 0 && reason.trim().length >= 3;
  // Shown before saving, because "47m becomes 67m" is the thing being agreed
  // with the customer standing at the counter.
  const after = batch.minutes_remaining + delta;

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!valid) return;
    setBusy(true);
    setError(null);
    try {
      const result = await api.recharges.adjust(batch.batch_id, {
        delta_minutes: delta,
        reason: reason.trim(),
      });
      toast.success(
        `${customerName} now has ${formatMinutes(result.batch.minutes_remaining)} on this recharge.`,
      );
      onSaved();
      onClose();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Adjust prepaid time" onClose={onClose}>
      <form onSubmit={submit} className="space-y-4">
        {error && <ErrorNote>{error}</ErrorNote>}

        <div className="rounded-lg border border-slate-800 bg-slate-900/60 px-3 py-2">
          <p className="text-sm text-slate-300">
            {batch.pack_label ?? 'Recharge'}{' '}
            <span className="text-xs text-slate-500">{POOL_LABEL[batch.pool]}</span>
          </p>
          <p className="text-xs text-slate-500">
            {formatMinutes(batch.minutes_remaining)} left of{' '}
            {formatMinutes(batch.minutes_purchased)} bought
          </p>
        </div>

        <div className="flex gap-2">
          {(['add', 'subtract'] as const).map((option) => (
            <button
              key={option}
              type="button"
              onClick={() => setDirection(option)}
              className={`flex-1 rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${
                direction === option
                  ? option === 'add'
                    ? 'border-emerald-500/60 bg-emerald-500/15 text-emerald-300'
                    : 'border-rose-500/60 bg-rose-500/15 text-rose-300'
                  : 'border-slate-800 text-slate-400 hover:bg-slate-800'
              }`}
            >
              {option === 'add' ? 'Add time' : 'Take back'}
            </button>
          ))}
        </div>

        <Input
          label="Minutes"
          value={amount}
          onChange={(event) => setAmount(event.target.value.replace(/[^0-9]/g, ''))}
          inputMode="numeric"
          placeholder="20"
          required
        />

        {/* The backend rejects a blank reason with a 400; saying so here means
            staff find out while they are typing, not after they submit. */}
        <Input
          label="Reason (recorded in the audit trail)"
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          placeholder="PC-03 crashed, returning time lost"
          required
        />

        {magnitude > 0 && (
          <p className="text-sm text-slate-400">
            {formatMinutes(batch.minutes_remaining)} →{' '}
            <span
              className={after < 0 ? 'text-rose-300' : 'font-semibold text-slate-100'}
            >
              {after < 0 ? 'below zero' : formatMinutes(after)}
            </span>
          </p>
        )}

        <div className="flex flex-col gap-2 sm:flex-row">
          <Button type="submit" className="flex-1" disabled={busy || !valid || after < 0}>
            Save adjustment
          </Button>
          <Button
            type="button"
            variant="secondary"
            className="flex-1"
            disabled={busy}
            onClick={onClose}
          >
            Cancel
          </Button>
        </div>
      </form>
    </Modal>
  );
}

// Ends a pack that has already been played on. What the customer pays for it
// is the admin's call — pro-rata is pre-filled as the usual fair answer, but
// full price or nothing at all are one tap away. The dialog says up front
// whether that changes the tab or comes back as credit, because that is what
// gets explained to the customer at the counter.
function RemoveBatchDialog({
  batch,
  customerName,
  onClose,
  onSaved,
}: {
  batch: Batch;
  customerName: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const toast = useToast();
  const price = batch.tab_price ?? Number(batch.amount_paid);
  const used = Math.max(batch.minutes_purchased - batch.minutes_remaining, 0);
  const proRata =
    batch.minutes_purchased > 0
      ? Math.min(Math.round((price * used) / batch.minutes_purchased), price)
      : price;
  const unpaid = batch.tab_paid === false;
  // A held pack is untouched by definition: removing it is a cancellation.
  const held = batch.status === 'on_hold';

  const [charge, setCharge] = useState(String(proRata));
  const [refundMode, setRefundMode] = useState<RefundMode>('credit');
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const chargeValue = Number(charge);
  const chargeValid = charge.trim() !== '' && Number.isFinite(chargeValue) && chargeValue >= 0;
  const overPrice = chargeValid && chargeValue > price;
  const valid = chargeValid && !overPrice && reason.trim().length >= 3;

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!valid) return;
    setBusy(true);
    setError(null);
    try {
      const result = await api.recharges.remove(batch.batch_id, {
        charge_amount: Math.round(chargeValue * 100) / 100,
        reason: reason.trim(),
        ...(unpaid ? {} : { refund_mode: refundMode }),
      });
      const refunded = result.refunded ?? result.credit_returned;
      toast.success(
        result.credit_returned > 0
          ? `${batch.pack_label ?? 'Recharge'} removed. ${formatMoney(result.credit_returned)} back to ${customerName} as credit.`
          : refunded > 0
            ? `${batch.pack_label ?? 'Recharge'} removed. Hand ${formatMoney(refunded)} back to ${customerName} ${refundMode === 'upi' ? 'by UPI' : 'in cash'} — it comes off today's revenue.`
            : `${batch.pack_label ?? 'Recharge'} removed for ${customerName}.`,
      );
      onSaved();
      onClose();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const presets: { label: string; value: number }[] = held
    ? [
        { label: 'Full refund', value: 0 },
        { label: 'No refund', value: price },
      ]
    : [
        { label: `Used ${formatMinutes(used)}`, value: proRata },
        { label: 'Full price', value: price },
        { label: 'Nothing', value: 0 },
      ];

  return (
    <Modal title={held ? 'Cancel pack on hold' : 'Remove recharge'} onClose={onClose}>
      <form onSubmit={submit} className="space-y-4">
        {error && <ErrorNote>{error}</ErrorNote>}

        <div className="rounded-lg border border-slate-800 bg-slate-900/60 px-3 py-2">
          <p className="text-sm text-slate-300">
            {batch.pack_label ?? 'Recharge'}{' '}
            <span className="text-xs text-slate-500">{POOL_LABEL[batch.pool]}</span>
          </p>
          <p className="text-xs text-slate-500">
            {formatMinutes(used)} played · {formatMinutes(batch.minutes_remaining)} left of{' '}
            {formatMinutes(batch.minutes_purchased)} · sold for {formatMoney(price)}
            {unpaid ? ' · not paid yet' : ' · paid'}
            {held && ' · on hold, never started'}
          </p>
        </div>

        <p className="text-sm text-rose-300">
          The {formatMinutes(batch.minutes_remaining)} left will be gone. If {customerName} is
          playing on this time, the session moves to their next pack or ends.
        </p>

        <div className="space-y-2">
          <span className="block text-sm font-medium text-slate-300">
            What should {customerName} pay for this pack?
          </span>
          <div className="flex gap-2">
            {presets.map((preset) => (
              <button
                key={preset.label}
                type="button"
                onClick={() => setCharge(String(preset.value))}
                className={`flex-1 rounded-lg border px-2 py-2 text-sm transition-colors ${
                  chargeValid && chargeValue === preset.value
                    ? 'border-sky-500/60 bg-sky-500/15 text-sky-200'
                    : 'border-slate-800 text-slate-400 hover:bg-slate-800'
                }`}
              >
                <span className="block font-medium">{formatMoney(preset.value)}</span>
                <span className="block text-xs">{preset.label}</span>
              </button>
            ))}
          </div>
          <Input
            value={charge}
            onChange={(event) => setCharge(event.target.value.replace(/[^0-9.]/g, ''))}
            inputMode="decimal"
            aria-label="Charge"
            placeholder="0"
            required
          />
        </div>

        <Input
          label="Reason (recorded in the audit trail)"
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          placeholder="Had to leave early, not coming back this week"
          required
        />

        {chargeValid && (
          <p className={`text-sm ${overPrice ? 'text-rose-300' : 'text-slate-400'}`}>
            {overPrice
              ? `The pack sold for ${formatMoney(price)} — the charge cannot be more than that.`
              : unpaid
                ? chargeValue === 0
                  ? 'It comes off the tab — nothing owed for it.'
                  : `The tab line drops from ${formatMoney(price)} to ${formatMoney(chargeValue)}.`
                : price - chargeValue > 0
                  ? refundMode === 'credit'
                    ? `Already paid — ${formatMoney(price - chargeValue)} goes back to ${customerName} as credit, and comes off today's revenue.`
                    : `Already paid — hand ${formatMoney(price - chargeValue)} back ${refundMode === 'upi' ? 'by UPI' : 'in cash'}. It comes off today's revenue and takings.`
                  : 'Already paid in full — nothing is refunded.'}
          </p>
        )}

        {!unpaid && chargeValid && !overPrice && price - chargeValue > 0 && (
          <div>
            <span className="mb-1.5 block text-sm font-medium text-slate-300">Refund as</span>
            <div className="grid grid-cols-3 gap-2">
              {(['credit', 'cash', 'upi'] as RefundMode[]).map((option) => (
                <button
                  key={option}
                  type="button"
                  onClick={() => setRefundMode(option)}
                  className={`min-h-11 rounded-lg border text-sm font-medium transition-colors ${
                    refundMode === option
                      ? 'border-sky-500/60 bg-sky-500/15 text-sky-300'
                      : 'border-slate-800 text-slate-300 hover:bg-slate-800'
                  }`}
                >
                  {option === 'credit' ? 'Credit' : option === 'cash' ? 'Cash' : 'UPI'}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="flex flex-col gap-2 sm:flex-row">
          <Button type="submit" variant="danger" className="flex-1" disabled={busy || !valid}>
            {held ? 'Cancel pack' : 'Remove recharge'}
          </Button>
          <Button
            type="button"
            variant="secondary"
            className="flex-1"
            disabled={busy}
            onClick={onClose}
          >
            Cancel
          </Button>
        </div>
      </form>
    </Modal>
  );
}

// Local calendar day as YYYY-MM-DD — what a date input holds and what the
// backend reads as the cafe's day.
const localDay = (date: Date = new Date()) => {
  const offset = date.getTimezoneOffset() * 60_000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 10);
};

// Starts a pack that was sold on hold. Today is the usual answer; an earlier
// day is for "it should have been started on Saturday". The validity counts
// from the chosen day, so the dialog says the expiry before anyone confirms.
function ActivateBatchDialog({
  batch,
  customerName,
  onClose,
  onSaved,
}: {
  batch: Batch;
  customerName: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const toast = useToast();
  const today = localDay();
  const soldOn = localDay(new Date(batch.purchased_at));
  const [day, setDay] = useState(today);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const validity = batch.validity_days ?? 0;
  const start = day === today ? new Date() : new Date(`${day}T00:00:00`);
  const expires = new Date(start.getTime() + validity * 86_400_000);
  const valid = day !== '' && day >= soldOn && day <= today && expires.getTime() > Date.now();
  const label = batch.pack_label ?? 'recharge';

  const activate = async () => {
    if (!valid) return;
    setBusy(true);
    setError(null);
    try {
      await api.recharges.activate(batch.batch_id, { activation_date: day });
      toast.success(
        `${customerName}'s ${label} is active — ${formatMinutes(batch.minutes_remaining)}, ` +
          `valid until ${formatDate(expires.toISOString())}.`,
      );
      onSaved();
      onClose();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={`Activate ${label}`} onClose={onClose}>
      <div className="space-y-4">
        {error && <ErrorNote>{error}</ErrorNote>}
        <div className="rounded-lg border border-slate-800 bg-slate-900/60 px-3 py-2 text-sm">
          <p className="text-slate-200">
            {formatMinutes(batch.minutes_remaining)} {POOL_LABEL[batch.pool]} · paid{' '}
            {formatMoney(batch.tab_price ?? batch.amount_paid)}
          </p>
          <p className="text-xs text-slate-500">
            Sold {formatDate(batch.purchased_at)} · valid {validity} day{validity === 1 ? '' : 's'}{' '}
            from activation
          </p>
        </div>

        <Input
          label="Activation date"
          type="date"
          value={day}
          min={soldOn}
          max={today}
          onChange={(event) => setDay(event.target.value)}
          hint="Validity counts from this day. Today starts it now; an earlier day starts it at the beginning of that day."
        />

        {day !== '' && (
          <p className={`text-sm ${valid ? 'text-slate-300' : 'text-rose-300'}`}>
            {day > today
              ? 'The activation date cannot be in the future.'
              : day < soldOn
                ? `It was sold on ${formatDate(batch.purchased_at)} — it cannot start before that.`
                : expires.getTime() <= Date.now()
                  ? 'Starting that early, it would already have expired. Pick a later day.'
                  : `Playable straight away, valid until ${formatDate(expires.toISOString())}.`}
          </p>
        )}

        <div className="flex flex-col gap-2 sm:flex-row">
          <Button className="flex-1" onClick={activate} disabled={busy || !valid}>
            {busy ? 'Activating…' : 'Activate'}
          </Button>
          <Button variant="secondary" className="flex-1" disabled={busy} onClick={onClose}>
            Cancel
          </Button>
        </div>
      </div>
    </Modal>
  );
}

const CUSTOMER_VIEWS = ['bill', 'time', 'purchases', 'sessions', 'profile'] as const;
type CustomerView = (typeof CUSTOMER_VIEWS)[number];

export function CustomerDetailPage() {
  const { customerId } = useParams<{ customerId: string }>();
  const [params, setParams] = useSearchParams();

  const [customer, setCustomer] = useState<CustomerCard | null>(null);
  const [history, setHistory] = useState<SessionHistory | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const sellPool = params.get('sell') as Pool | null;
  const view: CustomerView = CUSTOMER_VIEWS.includes(params.get('view') as CustomerView)
    ? (params.get('view') as CustomerView)
    : 'bill';
  const setView = (next: CustomerView) => {
    const updated = new URLSearchParams(params);
    if (next === 'bill') updated.delete('view');
    else updated.set('view', next);
    setParams(updated, { replace: true });
  };
  const [selling, setSelling] = useState<Pool | null>(sellPool);
  const [editing, setEditing] = useState(false);
  const [seating, setSeating] = useState(false);
  const [settingLogin, setSettingLogin] = useState(false);
  const [adjusting, setAdjusting] = useState<Batch | null>(null);
  const [removing, setRemoving] = useState<Batch | null>(null);
  const [activating, setActivating] = useState<Batch | null>(null);
  // Spend is masked until the eye is tapped — this page is often open on
  // the counter with the customer standing in front of it.
  const [showSpend, setShowSpend] = useState(false);
  const { isAdmin } = useAuth();

  const load = useCallback(async () => {
    if (!customerId) return;
    try {
      const [card, sessions] = await Promise.all([
        api.customers.get(customerId),
        api.sessions.history({ customer_id: customerId, limit: 20 }),
      ]);
      setCustomer(card);
      setHistory(sessions);
      setError(null);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, [customerId]);

  // Reload on any write in the app too — an order accepted from the header
  // bar changes the "Owes" figure up top without touching this page.
  const version = useDataVersion();
  useEffect(() => {
    void load();
  }, [load, version]);

  // The tab section owns its own fetch; this tells it a recharge sold or
  // adjusted from up here has changed the bill it is showing.
  const [tabVersion, setTabVersion] = useState(0);
  const reloadAll = useCallback(() => {
    setTabVersion((version) => version + 1);
    void load();
  }, [load]);

  const closeSell = () => {
    setSelling(null);
    if (params.get('sell')) {
      params.delete('sell');
      setParams(params, { replace: true });
    }
  };

  if (loading) return <Spinner label="Loading customer…" />;
  if (error) return <ErrorNote>{error}</ErrorNote>;
  if (!customer) return <EmptyState title="Customer not found." />;

  const liveBatches = customer.batches.filter(
    (batch) => batch.status === 'active' && batch.minutes_remaining > 0,
  );
  const heldBatches = customer.batches.filter(
    (batch) => batch.status === 'on_hold' && batch.minutes_remaining > 0,
  );
  const deadBatches = customer.batches.filter(
    (batch) =>
      !(batch.status === 'active' || batch.status === 'on_hold') || batch.minutes_remaining <= 0,
  );

  const owes = owedAfterCredit(customer.tab?.due_amount ?? 0, customer.credit_balance);
  const views: { id: CustomerView; label: string; badge?: ReactNode }[] = [
    {
      id: 'bill',
      label: 'Bill',
      badge: owes > 0 ? <Badge tone="amber">{formatMoney(owes)}</Badge> : undefined,
    },
    {
      id: 'time',
      label: 'Prepaid time',
      // Held packs are not in the balance above, so the tab has to say so.
      badge:
        heldBatches.length > 0 ? (
          <Badge tone="amber">{heldBatches.length} on hold</Badge>
        ) : liveBatches.length > 0 ? (
          <Badge>{liveBatches.length}</Badge>
        ) : undefined,
    },
    { id: 'purchases', label: 'Purchases' },
    { id: 'sessions', label: 'Sessions' },
    {
      id: 'profile',
      label: 'Profile',
      badge: customer.login_locked_until ? <Badge tone="amber">Locked</Badge> : undefined,
    },
  ];

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-slate-100">
            {customer.name}
            {!customer.is_active && (
              <span className="ml-2 align-middle">
                <Badge tone="red">Inactive</Badge>
              </span>
            )}
            {owedAfterCredit(customer.tab?.due_amount ?? 0, customer.credit_balance) > 0 && (
              <span className="ml-2 align-middle">
                {customer.tab_open_until &&
                new Date(customer.tab_open_until).getTime() < Date.now() ? (
                  <Badge tone="red">
                    Overdue {formatMoney(owedAfterCredit(customer.tab.due_amount, customer.credit_balance))} · was due {formatDate(customer.tab_open_until)}
                  </Badge>
                ) : (
                  <Badge tone="amber">Owes {formatMoney(owedAfterCredit(customer.tab.due_amount, customer.credit_balance))}</Badge>
                )}
              </span>
            )}
            {customer.credit_balance > 0 && (
              <span className="ml-2 align-middle">
                <Badge tone="sky">{creditLabel(customer.credit_balance, customer.tab?.due_amount ?? 0)}</Badge>
              </span>
            )}
          </h1>
          <p className="text-sm text-slate-500">
            {customer.phone} · joined {formatDate(customer.created_at)}
          </p>
          {customer.notes && (
            <p className="mt-1 text-sm text-slate-400">{customer.notes}</p>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="secondary" onClick={() => setEditing(true)}>
            Edit
          </Button>
          <Button variant="secondary" onClick={() => setSelling('general')}>
            Recharge
          </Button>
          {/* Seating from the card: the customer is at the counter, staff
              pick the seat. Hidden while they are already playing — one
              session per customer — and on an inactive account. */}
          {!customer.active_session && customer.is_active && (
            <Button onClick={() => setSeating(true)}>Start session</Button>
          )}
        </div>
      </div>

      {/* Both pools, side by side and always visible — a customer holding only
          PC minutes cannot be seated at the simulator. */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {(['general', 'simulator'] as Pool[]).map((pool) => {
          // Seconds, not minutes: the pool total less the partial minute this
          // customer has already played. Falls back to whole minutes so an
          // older backend still renders something sane.
          const secondsRemaining =
            pool === 'simulator'
              ? (customer.wallet.simulator_seconds_remaining ??
                 customer.wallet.simulator_minutes * 60)
              : (customer.wallet.general_seconds_remaining ??
                 customer.wallet.general_minutes * 60);
          const minutes = Math.floor(secondsRemaining / 60);
          return (
            <Card key={pool}>
              <p className="text-xs uppercase tracking-wide text-slate-500">
                {POOL_LABEL[pool]}
              </p>
              <p
                className={`mt-1 text-3xl font-semibold ${
                  minutes > 0 ? 'text-emerald-300' : 'text-slate-500'
                }`}
              >
                {formatBalance(secondsRemaining)}
              </p>
              <Button
                variant="ghost"
                className="mt-2 px-0"
                onClick={() => setSelling(pool)}
              >
                Recharge {POOL_LABEL_INLINE[pool]} →
              </Button>
            </Card>
          );
        })}
      </div>

      {/* Lifetime spend by kind, on the same basis as the revenue report.
          One slim row so it does not push the bill down. */}
      {customer.spend_totals && (
        <Card className="flex items-center gap-3 py-3">
          <div className="flex min-w-0 flex-1 flex-wrap items-baseline gap-x-6 gap-y-2">
            <div>
              <p className="text-xs uppercase tracking-wide text-slate-500">Spent here</p>
              <p className="text-xl font-semibold text-slate-100">
                {showSpend ? formatMoney(customer.spend_totals.total) : MONEY_MASK}
              </p>
            </div>
            {(
              [
                ['Recharges', customer.spend_totals.recharge],
                ['Food & drinks', customer.spend_totals.fnb],
                ['PS5', customer.spend_totals.ps5],
              ] as const
            ).map(([label, amount]) => (
              <div key={label}>
                <p className="text-xs text-slate-500">{label}</p>
                <p
                  className={`font-medium ${amount > 0 || !showSpend ? 'text-slate-200' : 'text-slate-600'}`}
                >
                  {showSpend ? formatMoney(amount) : MONEY_MASK}
                  {/* The share gives the figure away too, so it hides with it. */}
                  {showSpend && customer.spend_totals!.total > 0 && amount > 0 && (
                    <span className="ml-1 text-xs text-slate-500">
                      {Math.round((amount / customer.spend_totals!.total) * 100)}%
                    </span>
                  )}
                </p>
              </div>
            ))}
          </div>
          {/* One eye for every figure in the row, at the end of it. */}
          <RevealButton
            shown={showSpend}
            onToggle={() => setShowSpend((shown) => !shown)}
            what="spending"
          />
        </Card>
      )}

      {customer.active_session && (
        <Card className="border-emerald-500/50 bg-emerald-500/10">
          <p className="text-sm text-emerald-200">
            Playing now on{' '}
            <span className="font-semibold">
              {customer.active_session.station_name}
            </span>{' '}
            since {formatTime(customer.active_session.start_time)}.{' '}
            <Link to="/floor" className="underline">
              Open the floor
            </Link>{' '}
            to stop the session.
          </p>
        </Card>
      )}

      {/* Everything below the balances is one view at a time. Long-standing
          regulars have hundreds of purchases and sessions; stacked on one
          page, the bill — the thing staff actually came here for — ended up
          screens away from the balances. The choice lives in the URL so the
          back button and a reload keep it. */}
      <nav
        aria-label="Customer sections"
        className="-mx-4 flex gap-1 overflow-x-auto border-b border-slate-800 px-4"
      >
        {views.map((option) => (
          <button
            key={option.id}
            type="button"
            aria-current={view === option.id ? 'page' : undefined}
            onClick={() => setView(option.id)}
            className={`flex min-h-11 shrink-0 items-center gap-2 border-b-2 px-3 text-sm font-medium transition-colors ${
              view === option.id
                ? 'border-sky-400 text-sky-300'
                : 'border-transparent text-slate-400 hover:text-slate-200'
            }`}
          >
            {option.label}
            {option.badge}
          </button>
        ))}
      </nav>

      {view === 'bill' && (
        <CustomerTabSection
          customerId={customer.customer_id}
          customerName={customer.name}
          isActive={customer.is_active}
          onChanged={load}
          refreshKey={tabVersion}
        />
      )}
      {view === 'time' && (
        <section className="space-y-2">
          {/* Batches are shown individually, never as one number: 20 minutes
              expiring tomorrow and 5 hours expiring next month are not the same
              thing to a customer deciding what to play. */}
          {liveBatches.length === 0 ? (
            <EmptyState title="No prepaid time left.">
              {heldBatches.length > 0
                ? isAdmin
                  ? 'They have a pack on hold below — activate it, or recharge, before seating them.'
                  : 'They have a pack on hold below — ask an admin to activate it, or recharge.'
                : 'Recharge before seating this customer.'}
            </EmptyState>
          ) : (
            <ul className="divide-y divide-slate-800 rounded-xl border border-slate-800">
              {liveBatches.map((batch) => (
                <li
                  key={batch.batch_id}
                  className="flex flex-wrap items-center justify-between gap-3 px-4 py-3"
                >
                  <div>
                    <p className="font-medium text-slate-100">
                      {batch.pack_label ?? 'Recharge'}{' '}
                      <span className="text-xs text-slate-500">
                        {POOL_LABEL[batch.pool]}
                      </span>
                      {/* Packs drain soonest-expiry-first, re-checked every tick,
                          so the first live pack in each pool is the one being
                          (or about to be) used — even if bought last. */}
                      {liveBatches.find((other) => other.pool === batch.pool) === batch && (
                        <span className="ml-2 align-middle">
                          <Badge tone="sky">Used first</Badge>
                        </span>
                      )}
                    </p>
                    <p className="text-xs text-slate-500">
                      {formatMinutes(batch.minutes_remaining)} of{' '}
                      {formatMinutes(batch.minutes_purchased)} left ·{' '}
                      {formatMoney(batch.amount_paid)}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge
                      tone={
                        batch.days_until_expiry <= 3
                          ? 'amber'
                          : batch.days_until_expiry <= 7
                            ? 'sky'
                            : 'slate'
                      }
                    >
                      {batch.days_until_expiry <= 0
                        ? 'Expires today'
                        : `${batch.days_until_expiry} day${batch.days_until_expiry === 1 ? '' : 's'} left`}
                    </Badge>
                    {/* Admin-only, and the backend enforces it too — a staff
                        account that reaches the endpoint gets a 403. */}
                    {isAdmin && (
                      <Button
                        variant="ghost"
                        className="px-2 py-1 text-xs"
                        onClick={() => setAdjusting(batch)}
                      >
                        Adjust
                      </Button>
                    )}
                    {isAdmin && (
                      <Button
                        variant="ghost"
                        className="px-2 py-1 text-xs text-rose-300 hover:text-rose-200"
                        onClick={() => setRemoving(batch)}
                      >
                        Remove
                      </Button>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}

          {/* Sold and paid, waiting to start. Not in the balance, so they get
              their own block rather than hiding among the live packs. */}
          {heldBatches.length > 0 && (
            <ul className="divide-y divide-amber-500/20 rounded-xl border border-amber-500/40 bg-amber-500/5">
              {heldBatches.map((batch) => (
                <li
                  key={batch.batch_id}
                  className="flex flex-wrap items-center justify-between gap-3 px-4 py-3"
                >
                  <div>
                    <p className="font-medium text-slate-100">
                      {batch.pack_label ?? 'Recharge'}{' '}
                      <span className="text-xs text-slate-500">{POOL_LABEL[batch.pool]}</span>
                      <span className="ml-2 align-middle">
                        <Badge tone="amber">On hold</Badge>
                      </span>
                    </p>
                    <p className="text-xs text-slate-500">
                      {formatMinutes(batch.minutes_remaining)} · paid{' '}
                      {formatMoney(batch.tab_price ?? batch.amount_paid)} · sold{' '}
                      {formatDate(batch.purchased_at)} · valid {batch.validity_days ?? '?'} days from
                      activation
                    </p>
                  </div>
                  {isAdmin ? (
                    <div className="flex items-center gap-2">
                      <Button onClick={() => setActivating(batch)}>Activate</Button>
                      <Button
                        variant="ghost"
                        className="px-2 py-1 text-xs"
                        onClick={() => setAdjusting(batch)}
                      >
                        Adjust
                      </Button>
                      <Button
                        variant="ghost"
                        className="px-2 py-1 text-xs text-rose-300 hover:text-rose-200"
                        onClick={() => setRemoving(batch)}
                      >
                        Cancel
                      </Button>
                    </div>
                  ) : (
                    <p className="text-xs text-amber-300">An admin activates it.</p>
                  )}
                </li>
              ))}
            </ul>
          )}

          {deadBatches.length > 0 && (
            <details className="rounded-xl border border-slate-800 px-4 py-3">
              <summary className="cursor-pointer text-sm text-slate-400">
                {deadBatches.length} used or expired recharge
                {deadBatches.length === 1 ? '' : 's'}
              </summary>
              <ul className="mt-2 space-y-1 text-xs text-slate-500">
                {deadBatches.map((batch) => (
                  <li key={batch.batch_id}>
                    {batch.pack_label ?? 'Recharge'} · {POOL_LABEL[batch.pool]} ·{' '}
                    {batch.status} · bought {formatDate(batch.purchased_at)}
                  </li>
                ))}
              </ul>
            </details>
          )}
        </section>
      )}
      {view === 'purchases' && (
        <PurchaseHistorySection customerId={customer.customer_id} refreshKey={tabVersion} />
      )}
      {view === 'sessions' && (
        <section className="space-y-2">
          <p className="text-xs text-slate-500">The last 20 finished sessions, newest first.</p>
          {!history || history.sessions.length === 0 ? (
            <EmptyState title="No finished sessions yet." />
          ) : (
            <ul className="divide-y divide-slate-800 rounded-xl border border-slate-800">
              {history.sessions.map((session) => (
                <li
                  key={session.session_id}
                  className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 text-sm"
                >
                  <div>
                    <p className="text-slate-200">{session.station_name}</p>
                    <p className="text-xs text-slate-500">
                      {formatDateTime(session.start_time)} → {formatTime(session.end_time)}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-slate-200">
                      {formatMinutes(session.minutes_deducted)}
                    </p>
                    <p className="text-xs text-slate-500">{session.end_reason ?? '—'}</p>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>
      )}
      {view === 'profile' && (
        <div className="space-y-4">
          {/* Station sign-in. Shown on every card, because "can I just log in
              myself next time?" is asked at the counter, not planned ahead. */}
          <Card>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="text-xs uppercase tracking-wide text-slate-500">
                  Station sign-in
                </p>
                {customer.station_login ? (
                  <p className="mt-1 text-sm text-slate-200">
                    Signs in as{' '}
                    <span className="font-mono font-semibold">{customer.username}</span>
                    {customer.password_set_at && (
                      <span className="text-slate-500">
                        {' '}· password set {formatDate(customer.password_set_at)}
                      </span>
                    )}
                  </p>
                ) : (
                  <p className="mt-1 text-sm text-slate-500">
                    Not set up yet. Saving a username gives them password {DEFAULT_PASSWORD}.
                  </p>
                )}
                {customer.login_locked_until && (
                  <p className="mt-1 text-sm text-amber-300">
                    Locked after too many wrong passwords until{' '}
                    {formatTime(customer.login_locked_until)}. Setting a new
                    password unlocks it.
                  </p>
                )}
              </div>
              <Button variant="secondary" onClick={() => setSettingLogin(true)}>
                {customer.station_login ? 'Change / reset password' : 'Set up sign-in'}
              </Button>
            </div>
          </Card>

          {/* Lifetime play, per platform — the "how much do they come here?"
              question, answered without opening history. The session in progress
              counts live, so it keeps moving while they play. */}
          {customer.play_totals && (
            <Card>
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <p className="text-xs uppercase tracking-wide text-slate-500">Time played</p>
                <p className="text-sm text-slate-400">
                  <span className="text-lg font-semibold text-slate-100">
                    {formatMinutes(Math.floor(totalPlayedSeconds(customer.play_totals) / 60))}
                  </span>{' '}
                  in all
                </p>
              </div>
              <div className="mt-2 grid grid-cols-3 gap-3">
                {(['pc_standard', 'pc_simulator', 'ps5'] as const).map((type) => {
                  const total = customer.play_totals[type] ?? { sessions: 0, seconds_played: 0 };
                  return (
                    <div key={type}>
                      <p
                        className={`text-2xl font-semibold ${
                          total.seconds_played > 0 ? 'text-slate-100' : 'text-slate-600'
                        }`}
                      >
                        {formatMinutes(Math.floor(total.seconds_played / 60))}
                      </p>
                      <p className="text-xs text-slate-500">
                        {STATION_TYPE_LABEL[type]} · {total.sessions} session{total.sessions === 1 ? '' : 's'}
                      </p>
                    </div>
                  );
                })}
              </div>
            </Card>
          )}
        </div>
      )}

      {selling && (
        <SellRechargeDialog
          customer={customer}
          initialPool={selling}
          onClose={closeSell}
          onSold={reloadAll}
        />
      )}
      {seating && (
        <SeatCustomerDialog
          customer={customer}
          onClose={() => setSeating(false)}
          onStarted={reloadAll}
          onRecharge={(pool) => setSelling(pool)}
        />
      )}
      {editing && (
        <EditCustomerDialog
          customer={customer}
          onClose={() => setEditing(false)}
          onSaved={load}
        />
      )}
      {settingLogin && (
        <StationLoginDialog
          customer={customer}
          onClose={() => setSettingLogin(false)}
          onSaved={load}
        />
      )}
      {adjusting && (
        <AdjustBatchDialog
          batch={adjusting}
          customerName={customer.name}
          onClose={() => setAdjusting(null)}
          onSaved={reloadAll}
        />
      )}
      {activating && (
        <ActivateBatchDialog
          batch={activating}
          customerName={customer.name}
          onClose={() => setActivating(null)}
          onSaved={reloadAll}
        />
      )}
      {removing && (
        <RemoveBatchDialog
          batch={removing}
          customerName={customer.name}
          onClose={() => setRemoving(null)}
          onSaved={reloadAll}
        />
      )}
    </div>
  );
}
